import styles from '../../styles/Controls.module.css'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faFilter } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Col, Collapse, Container, Form, FormCheck, Row } from "react-bootstrap";
import { useAppContext } from "../../context/shared";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";
import { clickedCTA } from "../../store/actions/events";
import { ctaClickSources } from "../../lib/constants";
const Controls = (
  props: {
    count: number,
    ageGroups: {
      id: number
      minMonths: number,
      maxMonths: number,
      displayName: string,
    }[],
    authors: {
      id: number
      name: string
    }[],
    categories: {
      id: number
      name: string
    }[],
    selectedControls: {
      ageGroups: {}[],
      categories: {}[],
      authors: {}[],
      sort: string
    },
    onControlChange: any,
  }) => {
  const { localization } = useAppContext()
  const dispatch = useDispatch()
  const router = useRouter()
  const [open, setOpen] = useState<boolean>(false)

  const filtersToggled = useMemo(() => {
    return (["ageGroups", "categories", "authors"] as (keyof typeof props.selectedControls)[]).reduce((result: boolean, key) => {
      if (props.selectedControls[key].length) result = true
      return result
    }, false)
  }, [props.selectedControls])

  const filterList = useMemo(() => {
    const maxItemsToShow = 2
    const selectedAgeGroups = props.selectedControls.ageGroups.map(item => props.ageGroups.find(group => group.id == item)).map(item => item?.displayName)
    const selectedCategories = props.selectedControls.categories.map(item => props.categories.find(cat => cat.id == item)).map(item => item?.name)
    const selectedAuthors = props.selectedControls.authors.map(item => props.authors.find(author => author.id == item)).map(item => item?.name)
    const _filterList = [...selectedAgeGroups, ...selectedCategories, ...selectedAuthors]
    if (_filterList.length > maxItemsToShow) return _filterList.slice(0, maxItemsToShow).join(', ') + ', ...'
    return _filterList.join(", ")
  }, [props.selectedControls])

  const handleSortChange = (event: any) => {
    const nextSort = event.target.value
    if (nextSort === props.selectedControls.sort) return
    const nextSelectedControls = Object.assign({}, props.selectedControls, { sort: nextSort })
    props.onControlChange(nextSelectedControls)
  }

  const handleFilterChange = (group: "ageGroups" | "categories" | "authors") => (event: any) => {
    const id = event.target.value
    const checked = event.target.checked
    const nextSelectedControls = Object.assign({}, props.selectedControls)
    nextSelectedControls[group] = nextSelectedControls[group].filter(item => item != id)
    if (checked) nextSelectedControls[group].push(id)
    if (nextSelectedControls[group].length === props.selectedControls[group].length) return
    props.onControlChange(nextSelectedControls)
  }

  const clearFilters = () => {
    setOpen(false)
    props.onControlChange({
      ageGroups: [],
      categories: [],
      authors: [],
      sort: "newest"
    })
  }

  const toggle = () => {
    setOpen(!open)
    dispatch(clickedCTA({ name: ctaClickSources.blog.filters.name, url: router.asPath }))
  }

  return (
    <div>
      <Container>
        <Row className={'d-flex align-items-center justify-content-between py-3'}>
          <Col>
            <h2>{localization.blog_articles_list}</h2>
          </Col>
          <Col xs='6' lg='6'>
            <div className={`d-flex align-items-center gap-3 ${!filtersToggled ? 'justify-content-end' : ''}`}>
              {filtersToggled && <>
                <div className={[styles.resultsSummary, 'd-none d-lg-block text-truncate'].join(' ')}>{props.count} {localization.blog_results_for} {filterList}</div>
                <div className={[styles.clearFilters, 'clickable  ms-auto text-nowrap'].join(' ')} onClick={() => clearFilters()}>{localization.blog_clear_filters_btn}</div>
              </>
              }
              <div
                className={['d-flex align-items-center gap-2 clickable', styles.filterButton].join(' ')}
                onClick={() => toggle()}
              >
                <FontAwesomeIcon icon={faFilter} />
                <div>{localization.blog_filter_toggle_btn}</div>
                <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <Collapse in={open}>
        <div className={[styles.collapseContainer, (open ? 'mb-5' : '')].join(' ')}>
          <Container className='py-3 py-lg-5'>
            <Row className='gy-4'>
              <Col lg />
              <Col lg className='d-flex flex-column gap-2 pe-2'>
                <div className={styles.title}>
                  {localization.blog_sort_by_label}
                </div>
                <Form className='d-flex flex-column gap-2'>
                  <FormCheck
                    className={styles.item}
                    type='radio'
                    name="sort"
                    value='newest'
                    id='newest'
                    label={localization.blog_sort_by_newest}
                    checked={props.selectedControls.sort !== "oldest"}
                    onChange={handleSortChange}
                  />
                  <FormCheck
                    className={styles.item}
                    type='radio'
                    name="sort"
                    value='oldest'
                    id='oldest'
                    label={localization.blog_sort_by_oldest}
                    checked={props.selectedControls.sort === "oldest"}
                    onChange={handleSortChange}
                  />
                </Form>
              </Col>
              <Col lg className='d-flex flex-column gap-2'>
                {props.ageGroups.length > 0 && (
                  <div className='d-flex gap-2 flex-column'>
                    <div className={styles.title}>
                      {localization.blog_filter_by_age_group_label}
                    </div>
                    <Form className='d-flex flex-column gap-2'>
                      {props.ageGroups.map((ageGroup, index) => (
                        <FormCheck
                          key={index}
                          className={styles.item}
                          type='checkbox'
                          value={ageGroup.id}
                          id={ageGroup.displayName}
                          label={ageGroup.displayName}
                          checked={props.selectedControls.ageGroups.includes(ageGroup.id.toString())}
                          onChange={handleFilterChange("ageGroups")}
                        />
                      ))}
                    </Form>
                  </div>
                )}
              </Col>
              <Col lg className='d-flex flex-column gap-2'>
                {props.categories.length > 0 && (
                  <div className='d-flex gap-2 flex-column'>
                    <div className={styles.title}>
                      {localization.blog_filter_by_category_label}
                    </div>
                    <Form className='d-flex flex-column gap-2'>
                      {props.categories.map((category, index) => (
                        <FormCheck
                          key={index}
                          className={styles.item}
                          type='checkbox'
                          value={category.id}
                          id={category.name}
                          label={category.name}
                          checked={props.selectedControls.categories.includes(category.id.toString())}
                          onChange={handleFilterChange("categories")}
                        />
                      ))}
                    </Form>
                  </div>
                )}
              </Col>
              <Col lg className='d-flex flex-column gap-2'>
                {props.authors.length > 0 && (
                  <div className='d-flex gap-2 flex-column'>
                    <div className={styles.title}>
                      {localization.blog_filter_by_author_label}
                    </div>
                    <Form className='d-flex flex-column gap-2'>
                      {props.authors.map((author, index) => (
                        <FormCheck
                          key={index}
                          className={styles.item}
                          type='checkbox'
                          value={author.id}
                          id={author.name}
                          label={author.name}
                          checked={props.selectedControls.authors.includes(author.id.toString())}
                          onChange={handleFilterChange("authors")}
                        />
                      ))}
                    </Form>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </div>
      </Collapse>
    </div>
  )
}

export default Controls
